import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { buildUserActiveMap, isInactivePractitioner } from '@/common/practitionerStatus';
import { useSharedLocationRestriction } from '@/composables/useSharedLocationRestriction';
import { Practitioner } from '@/fhirworks';
import { computed, ref, watch } from 'vue';

/**
 * The Prescriber combobox: suggests active in-house providers and accepts free text for an outside
 * prescriber. `requester` is stored as `{ id, resourceType: 'Practitioner', display }` for a pick and
 * `{ display }` for free text.
 *
 * @param {object} options
 * @param {() => object|null} options.target - what the prescriber is written onto
 * @param {() => string} options.display - the prescriber the field currently shows
 */
export function usePrescriberPicker({ target, display }) {
    const { scopeUserUrl } = useSharedLocationRestriction();
    const prescriberOptions = ref([]);
    let prescriberOptionsRequested = false;

    // Fetched on first touch rather than on mount: this component renders in the Meds card and in every
    // form widget, and most of those never open the prescriber field. A failure retries next touch.
    const ensurePrescriberOptions = async () => {
        if (prescriberOptionsRequested) return;
        prescriberOptionsRequested = true;
        try {
            // In-app deactivation flips User.active, not Practitioner.active, so the linked Users are
            // what separates a current provider from a retired one.
            const response = await $httpFhirApi.get(scopeUserUrl('User?practitioner:missing=false&_include=practitioner'));
            const resources = Array.isArray(response.data) ? response.data : [];
            const userActiveById = buildUserActiveMap(resources);
            // fullName is a fhirworks getter, so the raw JSON the API returns has none — hydrate before
            // filtering on it or every provider drops out.
            prescriberOptions.value = resources
                .filter((res) => res.resourceType === 'Practitioner' && !isInactivePractitioner(res, userActiveById))
                .map((res) => new Practitioner(res))
                .filter((practitioner) => practitioner.fullName);
        } catch (err) {
            console.error('Failed to load prescriber options:', err);
            prescriberOptions.value = [];
            prescriberOptionsRequested = false;
        }
    };

    const prescriberSearch = ref('');
    const prescriberMenu = ref(false);
    const prescriberFocused = ref(false);

    // Whitespace is tidied when the field is left rather than while typing: a display trimmed mid-word
    // feeds model-value, the combobox resyncs its text from it, and the space just typed disappears.
    const trimOrClearPrescriber = (med) => {
        const shown = med?.requester?.display;
        if (typeof shown !== 'string') return;
        const trimmed = shown.trim();
        if (!trimmed) {
            med.requester = undefined;
            return;
        }
        if (trimmed === shown) return;
        // Named fields, not a spread: a fhirworks Reference's accessors don't enumerate, so spreading it
        // would silently drop a picked provider's id.
        const { id, resourceType } = med.requester;
        med.requester = id ? { id, resourceType, display: trimmed } : { display: trimmed };
    };

    const onPrescriberFocused = (isFocused) => {
        prescriberFocused.value = isFocused;
        if (isFocused) ensurePrescriberOptions();
        else trimOrClearPrescriber(target());
    };

    // The search term is only read, for filtering — never bound back into the combobox.
    const onPrescriberSearch = (term) => {
        prescriberSearch.value = term ?? '';
        ensurePrescriberOptions();
    };

    // Filtering happens here rather than in the combobox so the menu can stay shut when the typed name
    // matches nobody — left to itself Vuetify pops an empty list on every keystroke.
    const prescriberMatches = computed(() => {
        const term = prescriberSearch.value?.trim().toLowerCase();
        if (!term) return prescriberOptions.value;
        return prescriberOptions.value.filter((practitioner) => practitioner.fullName?.toLowerCase().includes(term));
    });

    // After a pick the search reads back the chosen provider's name — the field displaying its selection,
    // not someone searching. Only a pick carries an id: typed text is written into the value on every
    // keystroke, so it always equals the search and must not close the menu. Matches also change after
    // blur (the directory resolving late, the blur trim resyncing the search), so only a focused field opens.
    watch(prescriberMatches, (matches) => {
        const showingPick = !!target()?.requester?.id && prescriberSearch.value === display();
        prescriberMenu.value = prescriberFocused.value && matches.length > 0 && !showingPick;
    });

    // Only a picked provider carries an id — text that merely matches a provider's name stays free
    // text. Aidbox rejects null, so clearing the field removes the reference entirely.
    const setPrescriber = (value, med) => {
        if (!med) return;
        if (typeof value === 'string') {
            med.requester = value.trim() ? { display: value } : undefined;
            return;
        }
        if (value?.id) {
            med.requester = { id: value.id, resourceType: 'Practitioner', display: value.fullName };
            return;
        }
        med.requester = undefined;
    };

    return {
        prescriberOptions,
        prescriberMatches,
        prescriberMenu,
        prescriberSearch,
        ensurePrescriberOptions,
        onPrescriberFocused,
        onPrescriberSearch,
        setPrescriber,
        trimOrClearPrescriber,
    };
}
